import { useState, type CSSProperties } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DEPLOYMENT_KIND_LABELS, type CalendarEventDto } from '@bhops/shared';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Form';
import { TimeSelect } from '@/components/TimeSelect';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useOptionalToast } from '@/components/Toast';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { extractApiError } from '@/api/client';
import { kstYmdHyphen } from '@/lib/format-date';
import {
  EventScheduleFields,
  eventExtras,
  eventFieldsReady,
  eventRobotLines,
  type EventFieldsValue,
} from './EventScheduleFields';

// 이벤트 일정 보기 · 고치기(2026-11-02) — 캘린더의 [이벤트 설치] · [이벤트 회수] 칩을 누르거나 우클릭
// '수정'으로 연다. 설치 · 회수 일시, 유형, 장소, 지도 링크, 내용, 출고할 로봇, 준비물, 비고를 한 창에서
// 고친다. 배정된 로봇은 여기서 보기만 한다(배정은 로봇 배정 화면). 설치 완료 · 회수 완료는 칩 우클릭.

const ymd = (iso: string) => kstYmdHyphen(iso) ?? '';

export function EventEditModal({
  event,
  onClose,
}: {
  event: CalendarEventDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [installDate, setInstallDate] = useState(ymd(event.installDate));
  const [installTime, setInstallTime] = useState(event.installTime ?? '');
  const [fields, setFields] = useState<EventFieldsValue>(() => ({
    returnDate: ymd(event.returnDate),
    returnTime: event.returnTime ?? '',
    kind: event.kind,
    location: event.location,
    mapUrl: event.mapUrl ?? '',
    content: event.content,
    note: event.note ?? '',
    checklist: event.checklist,
    lines: Object.fromEntries(event.robotLines.map((l) => [l.modelId, l.quantity])),
  }));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      scheduleDatesApi.updateEvent(event.id, {
        location: fields.location.trim(),
        content: fields.content.trim(),
        installDate,
        installTime: installTime || null,
        returnDate: fields.returnDate,
        returnTime: fields.returnTime || null,
        checklist: fields.checklist,
        robotLines: eventRobotLines(fields),
        ...eventExtras(fields),
      }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['assignments'] }),
      ]);
      toast?.success('이벤트를 고쳤습니다. 1분 안에 구글 캘린더에도 반영됩니다.');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="이벤트"
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            닫기
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!eventFieldsReady(installDate, fields) || save.isPending}
            data-testid="event-edit-save"
          >
            {save.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={robotsBox} data-testid="event-edit-robots">
          <span style={{ fontWeight: 600 }}>{event.returned ? '나갔던 로봇' : '배정된 로봇'}</span>
          <span style={{ color: event.robots.length ? 'var(--fg-default)' : 'var(--fg-muted)' }}>
            {event.robots.length
              ? event.robots.map((r) => [r.modelName, r.label].filter(Boolean).join(' ')).join(', ')
              : event.robotLines.length
                ? '아직 배정 전입니다. 제조팀이 로봇 배정 화면에서 배정합니다.'
                : '출고할 로봇이 없습니다.'}
          </span>
          <span style={{ color: 'var(--fg-muted)' }} data-testid="event-edit-stage">
            {event.returned
              ? '회수를 마쳐 배정이 풀렸습니다. 출고 이력에 반납으로 남았습니다.'
              : event.installed
                ? '설치 완료 — 로봇 출고 이력에 기록됐습니다. 회수하면 [이벤트 회수] 일정을 우클릭해 [회수 완료]를 누르세요.'
                : '설치하면 [이벤트 설치] 일정을 우클릭해 [설치 완료]를 누르세요. 로봇 출고 이력에 남습니다.'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ ...field, flex: '1 1 150px' }}>
            <span style={labelSt}>설치 날짜</span>
            <input
              type="date"
              value={installDate}
              onChange={(e) => setInstallDate(e.target.value)}
              style={input}
              data-testid="event-install-date"
            />
          </label>
          <label style={{ ...field, flex: '2 1 260px' }}>
            <span style={labelSt}>
              설치 시간 <span style={{ fontWeight: 400, color: 'var(--fg-muted)' }}>(선택)</span>
            </span>
            <TimeSelect
              value={installTime}
              onChange={setInstallTime}
              style={input}
              ariaLabel="설치 시간 (선택)"
              testId="event-install-time"
            />
          </label>
        </div>
        <EventScheduleFields installDate={installDate} value={fields} onChange={setFields} />
        {error && (
          <div role="alert" style={errorSt} data-testid="event-edit-error">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * 이벤트의 [설치 완료] · [회수 완료]와 그 되돌리기(2026-11-02 오너 — 로봇 배치의 행사 · 전시와 "연결하기로").
 * 우클릭 메뉴에서 연다. 설치 완료는 배정된 로봇마다 출고 기록을, 회수 완료는 그 출고의 반납과 정비 입고를 남긴다.
 */
export function EventStageDialog({
  event,
  stage,
  undo = false,
  onClose,
}: {
  event: CalendarEventDto;
  stage: 'install' | 'return';
  /** true 면 되돌리기. */
  undo?: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [busy, setBusy] = useState(false);
  const labels = event.robots.map((r) => r.label).join(', ');
  const kind = DEPLOYMENT_KIND_LABELS[event.kind];

  async function confirm() {
    setBusy(true);
    try {
      if (stage === 'install') {
        if (undo) await scheduleDatesApi.undoInstallEvent(event.id);
        else await scheduleDatesApi.installEvent(event.id);
      } else if (undo) await scheduleDatesApi.undoReturnEvent(event.id);
      else await scheduleDatesApi.returnEvent(event.id);
    } catch (err) {
      setBusy(false);
      throw new Error(extractApiError(err));
    }
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
      qc.invalidateQueries({ queryKey: ['assignments'] }),
      qc.invalidateQueries({ queryKey: ['robots'] }),
      qc.invalidateQueries({ queryKey: ['robot-deployments'] }),
      qc.invalidateQueries({ queryKey: ['deployments'] }),
    ]);
    toast?.success(DONE[stage][undo ? 'undo' : 'do']);
    setBusy(false);
    onClose();
  }

  const robots = labels ? ` ${labels}` : '';
  const message =
    stage === 'install'
      ? undo
        ? `${event.location} 이벤트의 설치 완료를 되돌립니다.${robots ? `${robots} 로봇의 출고 기록을 없던 일로 하고 재고로 돌립니다. 배정은 그대로 남습니다.` : ''}`
        : `${event.location} 이벤트의 설치를 마칩니다.${robots ? `${robots} 로봇이 '${kind}' 출고로 기록되고 상태는 '기타'가 됩니다.` : ''}`
      : undo
        ? `${event.location} 이벤트의 회수 완료를 되돌립니다.${robots ? `${robots} 로봇이 다시 이벤트 현장에 나가 있는 것으로 돌아갑니다.` : ''}`
        : `${event.location} 이벤트의 회수를 마칩니다.${robots ? `${robots} 로봇의 출고를 반납 처리하고 정비 상태로 넣습니다.` : ''}${event.installed ? '' : ' 설치 완료를 누르지 않아 출고 기록도 함께 남깁니다.'}`;

  return (
    <ConfirmDialog
      open
      title={
        stage === 'install'
          ? undo
            ? '설치 완료 되돌리기'
            : '이벤트 설치 완료'
          : undo
            ? '회수 완료 되돌리기'
            : '이벤트 회수 완료'
      }
      message={message}
      confirmLabel={undo ? '되돌리기' : stage === 'install' ? '설치 완료' : '회수 완료'}
      busy={busy}
      onConfirm={confirm}
      onCancel={onClose}
    />
  );
}

const DONE = {
  install: {
    do: '설치를 마쳤습니다. 로봇 출고 이력에 기록했습니다.',
    undo: '설치 완료를 되돌렸습니다. 로봇은 재고로 돌아갔습니다.',
  },
  return: {
    do: '이벤트 회수를 마쳤습니다. 나갔던 로봇은 정비 상태가 됐습니다.',
    undo: '회수 완료를 되돌렸습니다.',
  },
} as const;

const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const labelSt: CSSProperties = { fontSize: 13, fontWeight: 600 };
const input: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '6px 10px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
  boxSizing: 'border-box',
};
const robotsBox: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-sunken)',
  fontSize: 13,
};
const errorSt: CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  fontSize: 13,
};
